import { useCallback, useEffect, useState } from 'react';
import { listOtherSaleProducts, setOtherSaleProductActive, updateOtherSaleProduct } from '../api/otherSales';
import { getRecipes } from '../api/inventory';
import { getErrorMessage } from '../api/client';
import { useConfirm } from './ui/ConfirmDialog';
import { CheckGroup, CheckRow } from './ui/CheckRow';
import { DenseList, DenseRow } from './ui/DenseList';
import { MetaList } from './ui/MetaList';
import { Button } from './ui/primitives';
import { useShellTier } from '../layouts/shellTier';
import { Modal } from './Modal';
import { ActionBar } from './ui/ActionBar';
import type { OtherSaleProduct } from '../types/otherSales';
import type { Recipe } from '../types/inventory';
import type { Store } from '../types/stores';
import { formatMoney } from '../utils/costFormatting';
import {
  PRODUCT_COST_MODE_OPTIONS,
  PRODUCT_TAX_MODE_OPTIONS,
  buildProductPayload,
  formatProductCost,
  formatStoreAssignments,
  productSaveDisabledReason,
  validateProductForm,
  type ProductFormState,
} from '../utils/otherSaleProductForm';
import { costChangeConfirmation, eligibleRecipeOptions } from '../utils/otherSaleRecipeAssignment';
import { openNewProductFromCatalog } from '../utils/newProductRoute';

const TAX_MODE_LABELS = Object.fromEntries(PRODUCT_TAX_MODE_OPTIONS) as Record<string, string>;

/**
 * Fase 21C-4A: administración visual del catálogo de Productos Otros (base del mini-POS). Solo se
 * monta para OWNER/MANAGER (el llamador ya valida el permiso; el backend es la seguridad real).
 * NUNCA llama al checkout ni crea ventas — solo CRUD del catálogo.
 */
export function OtherSaleProductsAdmin({ stores }: { stores: Store[] }) {
  const confirm = useConfirm();
  const tier = useShellTier();
  const [products, setProducts] = useState<OtherSaleProduct[]>();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  // Rediseño · Bloque E: dar de alta ya no es este modal sino «Nuevo producto» (P-029), con ruta
  // propia. El modal queda solo para EDITAR, que no es de este bloque: una sola alta, no dos.
  const [editing, setEditing] = useState<OtherSaleProduct | undefined>();

  const load = useCallback(async () => {
    setError(undefined);
    try {
      // includeInactive: los administradores ven activos e inactivos para poder reactivar.
      const result = await listOtherSaleProducts({ includeInactive: true });
      setProducts(result.items);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar el catálogo de productos.'));
      setProducts([]);
    }
  }, []);

  useEffect(() => {
    void load();
    // Recetas para el selector opcional; si falla, el catálogo sigue usable sin recetas.
    void getRecipes().then((result) => setRecipes(result.recipes)).catch(() => setRecipes([]));
  }, [load]);

  const handleToggleActive = async (product: OtherSaleProduct) => {
    if (product.isActive && !(await confirm({ title: 'Desactivar producto', description: `¿Desactivar "${product.name}"? Dejará de aparecer para vender, pero no se borra y puedes reactivarlo después.`, confirmLabel: 'Desactivar', danger: true }))) return;
    setBusyId(product.id);
    setError(undefined);
    try {
      const result = await setOtherSaleProductActive(product.id, !product.isActive);
      setNotice(result.message);
      await load();
    } catch (toggleError) {
      setError(getErrorMessage(toggleError, 'No fue posible cambiar el estado del producto.'));
    } finally {
      setBusyId(undefined);
    }
  };

  return <section className="other-sale-products-admin" aria-label="Catálogo de Productos Otros">
    <div className="stock-heading">
      <div><strong>Catálogo de productos</strong><span>Lo que se vende en el punto de venta. Precio e IVA se toman de aquí al vender.</span></div>
      <button className="button button--primary" onClick={openNewProductFromCatalog} type="button">+ Nuevo producto</button>
    </div>

    {error && <div className="alert alert--error">{error}</div>}
    {notice && <div className="alert alert--success">{notice}</div>}

    {products === undefined
      ? <div className="inline-loading"><span className="spinner" />Cargando catálogo...</div>
      : products.length === 0
        ? <div className="empty-panel"><strong>Sin productos en el catálogo</strong><span>Crea tu primer producto para poder venderlo desde el punto de venta.</span></div>
        // P-076: en el teléfono, una fila compacta por producto (nombre, IVA y costo resumidos, precio, estado y
        // Editar a la vista; tiendas, receta y orden tras «Ver detalles»). Son los mismos ocho datos de la tabla,
        // que se conserva en tableta y escritorio.
        : tier === 'compact'
          ? <DenseList label="Catálogo de productos" separated>{products.map((product) => {
            const recipe = product.recipeId ? recipes.find((item) => item.id === product.recipeId) : undefined;
            const taxLabel = `${TAX_MODE_LABELS[product.taxMode] ?? product.taxMode}${product.taxMode === 'CUSTOM' && product.taxPercentage ? ` (${Number(product.taxPercentage).toFixed(1)}%)` : ''}`;
            const costLabel = formatProductCost(product, recipe?.name ?? product.recipeName);
            return <DenseRow
              actions={<>
                <Button disabled={busyId === product.id} onClick={() => setEditing(product)} variant="ghost">Editar</Button>
                <Button disabled={busyId === product.id} onClick={() => void handleToggleActive(product)} variant={product.isActive ? 'danger' : 'ghost'}>{product.isActive ? 'Desactivar' : 'Reactivar'}</Button>
              </>}
              badge={<span className={`consumption-status consumption-status--${product.isActive ? 'applied' : 'reverted'}`}>{product.isActive ? 'Activo' : 'Inactivo'}</span>}
              details={<MetaList items={[
                { label: 'Tiendas', value: formatStoreAssignments(product.storeIds, stores) },
                { label: 'Costo', value: costLabel },
                { label: 'IVA', value: taxLabel },
                { label: 'Orden de visualización', value: product.displayOrder },
              ]} />}
              inactive={!product.isActive}
              key={product.id}
              name={product.name}
              subtitle={`${taxLabel} · ${costLabel === '—' ? 'Sin costo' : costLabel}`}
              title={product.name}
              value={formatMoney(product.price)}
            />;
          })}</DenseList>
          : <div className="data-table-wrap"><table className="data-table"><thead><tr>
            <th>Orden</th><th>Nombre</th><th>Precio</th><th>IVA</th><th>Tiendas</th><th>Costo</th><th>Estado</th><th>Acciones</th>
          </tr></thead><tbody>{products.map((product) => {
            const recipe = product.recipeId ? recipes.find((item) => item.id === product.recipeId) : undefined;
            return <tr className={product.isActive ? '' : 'data-row--inactive'} key={product.id}>
              <td>{product.displayOrder}</td>
              <td>{product.name}</td>
              <td>{formatMoney(product.price)}</td>
              <td>{TAX_MODE_LABELS[product.taxMode] ?? product.taxMode}{product.taxMode === 'CUSTOM' && product.taxPercentage ? ` (${Number(product.taxPercentage).toFixed(1)}%)` : ''}</td>
              <td>{formatStoreAssignments(product.storeIds, stores)}</td>
              {/* P-071: receta, costo aproximado o sin definir. */}
              <td>{formatProductCost(product, recipe?.name ?? product.recipeName)}</td>
              <td><span className={`consumption-status consumption-status--${product.isActive ? 'applied' : 'reverted'}`}>{product.isActive ? 'Activo' : 'Inactivo'}</span></td>
              <td><div className="table-actions">
                <button className="text-button" disabled={busyId === product.id} onClick={() => setEditing(product)} type="button">Editar</button>
                <button className={`text-button${product.isActive ? ' text-button--danger' : ''}`} disabled={busyId === product.id} onClick={() => void handleToggleActive(product)} type="button">{product.isActive ? 'Desactivar' : 'Reactivar'}</button>
              </div></td>
            </tr>;
          })}</tbody></table></div>}

    {editing && <OtherSaleProductFormModal
      product={editing}
      stores={stores}
      recipes={recipes}
      onClose={() => setEditing(undefined)}
      onSaved={async (message) => { setEditing(undefined); setNotice(message); await load(); }}
    />}
  </section>;
}

function toFormState(product: OtherSaleProduct): ProductFormState {
  return {
    name: product.name,
    price: product.price,
    taxMode: product.taxMode,
    taxPercentage: product.taxMode === 'CUSTOM' ? (product.taxPercentage ?? '') : '',
    displayOrder: String(product.displayOrder),
    // P-071: el modo lo manda el servidor ya resuelto; la cifra solo llega a quien puede ver costos.
    costMode: product.costMode,
    recipeId: product.recipeId ?? '',
    approximateUnitCost: product.approximateUnitCost ?? '',
    storeIds: product.storeIds,
  };
}

// Rediseño · Bloque E: este modal ya solo EDITA. El alta es «Nuevo producto» (pages/NuevoProductoPage).
function OtherSaleProductFormModal({ product, stores, recipes, onClose, onSaved }: {
  product: OtherSaleProduct;
  stores: Store[];
  recipes: Recipe[];
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
}) {
  const [form, setForm] = useState<ProductFormState>(() => toFormState(product));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  // Fase 22C-2 / P-071: confirmación explícita al cambiar cómo se lleva el costo —receta, costo
  // aproximado o ninguno— o su valor (solo afecta futuras ventas). D-P071-10: la edición no se
  // rediseña; cambia de modo con la misma regla que el alta (el servidor es quien la aplica).
  const originalRecipeId = product.recipeId ?? '';
  const [confirmRecipeChange, setConfirmRecipeChange] = useState(false);
  const original = { costMode: product.costMode, recipeId: originalRecipeId, approximateUnitCost: product.approximateUnitCost ?? '' };
  const confirmation = costChangeConfirmation(original, { costMode: form.costMode, recipeId: form.recipeId, approximateUnitCost: form.approximateUnitCost });
  const recipeOptions = eligibleRecipeOptions(recipes, originalRecipeId || null);
  const needsRecipeConfirmation = confirmation !== null;
  // ZTV-07: la razón por la que Guardar está deshabilitado siempre es visible (null = habilitado).
  const saveReason = productSaveDisabledReason(form, needsRecipeConfirmation, confirmRecipeChange);

  const toggleStore = (storeId: string) => {
    setForm((current) => ({
      ...current,
      storeIds: current.storeIds.includes(storeId)
        ? current.storeIds.filter((id) => id !== storeId)
        : [...current.storeIds, storeId],
    }));
  };

  const save = async () => {
    // Prevención de doble envío: si ya está guardando, se ignora el segundo clic.
    if (isSaving) return;
    const validationError = validateProductForm(form);
    if (validationError) { setError(validationError); return; }
    setIsSaving(true);
    setError(undefined);
    try {
      const payload = buildProductPayload(form);
      // Fase 22C-2: control optimista — envía el updatedAt con el que se abrió el formulario.
      await updateOtherSaleProduct(product.id, { ...payload, expectedUpdatedAt: product.updatedAt });
      await onSaved('Producto actualizado.');
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible guardar el producto.'));
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    title="Editar producto"
    isBusy={isSaving}
    onClose={onClose}
    // ZTV-07: patrón Ticket Mixto — un Guardar deshabilitado siempre explica qué falta.
    // P-076: el motivo ocupa su propio renglón del pie, no una columna junto a los botones.
    // P-078 (ActionBar v2): «Guardar cambios» a todo el ancho en el teléfono (a 360 px se partía en dos líneas).
    footer={<ActionBar aria-label="Acciones del producto" note={saveReason && !isSaving ? <div className="ticket-verifier ticket-verifier--pending">{saveReason}</div> : undefined} primary={<button className="button button--primary" disabled={isSaving || saveReason !== null} onClick={() => void save()} type="button">{isSaving ? 'Guardando...' : 'Guardar cambios'}</button>}>
      <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
    </ActionBar>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    <div className="form-grid form-grid--compact">
      <label className="field"><span>Nombre</span><input autoFocus onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Producto o servicio que vendes" value={form.name} /></label>
      <label className="field"><span>Precio {form.taxMode === 'CUSTOM' ? '(subtotal, sin IVA)' : ''}</span><input min="0" onChange={(event) => setForm({ ...form, price: event.target.value })} placeholder="0.00" step="any" type="number" value={form.price} /></label>
      <label className="field"><span>IVA</span><select onChange={(event) => setForm({ ...form, taxMode: event.target.value as ProductFormState['taxMode'] })} value={form.taxMode}>{PRODUCT_TAX_MODE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      {form.taxMode === 'CUSTOM' && <label className="field"><span>Porcentaje IVA</span><input min="0" max="100" onChange={(event) => setForm({ ...form, taxPercentage: event.target.value })} placeholder="16" step="any" type="number" value={form.taxPercentage} /></label>}
      <label className="field"><span>Orden de visualización</span><input min="0" onChange={(event) => setForm({ ...form, displayOrder: event.target.value })} step="1" type="number" value={form.displayOrder} /></label>
      <label className="field"><span>Costo</span><select onChange={(event) => { setForm({ ...form, costMode: event.target.value as ProductFormState['costMode'] }); setConfirmRecipeChange(false); }} value={form.costMode}>
        {PRODUCT_COST_MODE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select><small>Una sola forma a la vez: con receta, el costo sale de la receta; con costo aproximado, no se descuenta inventario.</small></label>
      {form.costMode === 'APPROXIMATE' && <label className="field"><span>Costo aproximado por unidad</span><input min="0.01" onChange={(event) => { setForm({ ...form, approximateUnitCost: event.target.value }); setConfirmRecipeChange(false); }} placeholder="0.00" step="any" type="number" value={form.approximateUnitCost} /><small>Mayor que cero. Se aplicará únicamente a ventas futuras.</small></label>}
      {form.costMode === 'RECIPE' && <label className="field"><span>Receta de inventario</span><select onChange={(event) => { setForm({ ...form, recipeId: event.target.value }); setConfirmRecipeChange(false); }} value={form.recipeId}>
        <option value="">Elige una receta</option>
        {recipeOptions.map((option) => <option key={option.id} value={option.id} disabled={option.disabled}>{option.label}</option>)}
      </select><small>La receta se aplicará únicamente a ventas futuras.</small></label>}
    </div>
    {needsRecipeConfirmation && <div className="alert alert--warning" role="alert">
      <p>{confirmation}</p>
      <CheckRow checked={confirmRecipeChange} label="Entiendo: este cambio solo afecta ventas futuras." onChange={setConfirmRecipeChange} plain />
    </div>}
    {/* P-076: renglones de selección (casilla y nombre juntos, toda la fila tocable). Antes, `.field input`
        deformaba cada casilla a una caja de 346 × 48 px con el nombre debajo. La semántica no cambia: sin
        selección = disponible en todas las tiendas. */}
    <CheckGroup hint="Sin selección = disponible en todas las tiendas del negocio." legend="Tiendas donde se vende">
      {stores.map((store) => <CheckRow checked={form.storeIds.includes(store.id)} key={store.id} label={store.name} onChange={() => toggleStore(store.id)} />)}
    </CheckGroup>
  </Modal>;
}
